// src/components/MenuTabs.jsx
import { useMemo, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import '../style/Menu.css';

// Sample product images
import menuCategories from '../data/data';


const Menu = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const navigate = useNavigate()

  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleProductClick = (product) => {
    const categoryName = menuCategories[activeTab].name
    navigate('/view-product', { 
      state: { product , categoryName } 
    });
  };

  const closeModal = () => {
    setSelectedProduct(null); // Closes the modal
  };

  // Get current items for pagination
   const { currentItems, totalPages } = useMemo(() => {
    const items = menuCategories[activeTab].items;
    return {
      currentItems: items.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
      totalPages: Math.ceil(items.length / itemsPerPage)
    };
  }, [activeTab, currentPage, menuCategories]);

  const handlePageChange = (newPage) => {
    // Only update if page actually changes
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div id="menu">
      <MenuSection>
        <SectionHeader>
          <Title>Our Menu</Title>
          <Divider />
        </SectionHeader>

        <TabsContainer>
          <Tabs>
            {menuCategories.map((category, index) => (
              <Tab 
                key={index} 
                active={activeTab === index}
                onClick={() => {
                  setActiveTab(index);
                  setCurrentPage(1); // Reset to first page when changing tabs
                }}
              >
                <TabIcon>{category.icon}</TabIcon>
                <div style={{fontFamily: "Cairo Play"}}>{category.name}</div>
              </Tab>
            ))}
          </Tabs>

          <TabContent id='tab-content'>
            {currentItems.map((item, index) => (
             <ProductCard id='product-card' onClick={() => handleProductClick(item)}>  
                {
                  item.image &&
                   <ImageDiv>
                     <ProductImage src={item.image} alt={item.name} />
                   </ImageDiv>
                   
                }
                <ProductInfo id='product-info'>
                  <ProductHeader>
                    <ProductName id='ProductName'>{item.name}</ProductName>
                    <ProductPrice id='ProductPrice'>{item.price}</ProductPrice>
                  </ProductHeader>
                  <ProductDescription>{item.description}</ProductDescription>
                </ProductInfo>
              </ProductCard>
            ))}
          </TabContent>

          {totalPages > 1 && (
            <Pagination>
              <PaginationButton 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </PaginationButton>
              
              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationButton
                  key={i}
                  active={currentPage === i + 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </PaginationButton>
              ))}
              
              <PaginationButton
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </PaginationButton>
            </Pagination>
          )}
        </TabsContainer> 
      </MenuSection>
      {selectedProduct && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-wrapper">
              <CloseButton onClick={closeModal}>×</CloseButton>
              <img 
                src={selectedProduct.image} 
                alt={selectedProduct.name}
                className="modal-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// const CloseButton = styled.button`
//   background: white;
//   border: none;
//   font-size: 24px;
//   color: red;
//   cursor: pointer;
//   transition: all 0.2s;
//   width:50%;
//   margin:auto;
//   height:20px

//   &:hover {
//     color: #333;
//     transform: scale(1.1);
//   }

//   &:focus {
//     outline: none;
//   }
// `;

const CloseButton = styled.button`
 position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
`;
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 1rem;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: ${props => props.active ? '#c57b57' : '#f0e6d9'};
  color: ${props => props.active ? 'white' : '#2a1a1f'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.active ? 'bold' : 'normal'};

  &:hover:not(:disabled) {
    background-color: #c57b57;
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MenuSection = styled.section`
  padding: 4rem 1rem;
  background-color: #f9f5f0;
  margin-top : 70px
`;

const Divider = styled.div`
  width: 60px;
  height: 3px;
  background-color: #c57b57;
  margin: 1.5rem auto;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: #c57b57;
    border-radius: 50%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #2a1a1f;
  margin-bottom: 0.5rem;
  font-family: "Playwrite AU SA", sans-serif;
`;

const TabsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tab = styled.button`
  padding: 0.8rem 1.5rem;
  background: ${props => props.active ? '#c57b57' : 'white'};
  color: ${props => props.active ? 'white' : '#2a1a1f'};
  border: none;
  border-radius: 30px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &:hover {
    background: ${props => props.active ? '#c57b57' : '#f0e6d9'};
  }
`;

const TabIcon = styled.span`
  font-size: 1rem;
`;

const TabContent = styled.div`
  display: flex;
  flex-wrap:wrap;
  gap: 1rem;
  justify-content: center;
`;

const ProductCard = styled.div`
  background: transparent;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  transition: transform 0.3s ease;
  display :flex;
  width:30%;
`;

const ImageDiv = styled.div`
width:100px;
max-height:100px;
border-bottom:5px solid #c57b57;
padding:3px;
`;

const ProductImage = styled.img`
  height : 100%;
  object-fit: cover;
  width: 100%;
`;

const ProductInfo = styled.div`
  padding: 0.8rem;
  width:100%;
  display:grid
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  border-bottom:2px dashed #C57B57;
  padding-bottom:0.5rem
`;

const ProductName = styled.h3`
  font-size: 1.2rem;
  color: #2a1a1f;
  margin: 0;
  font-family: "Playwrite AU SA", sans-serif;
  font-family: "Cairo Play", sans-serif;
`;

const ProductPrice = styled.span`
  font-weight: bold;
  color: #c57b57;
  font-family: "Playwrite AU SA", sans-serif;
  font-size:13px
  
`;

const ProductDescription = styled.p`
  color: #666;
  margin: 0;
  line-height: 1.6;
  font-family: "Cairo Play", sans-serif;
`;

export default Menu;